import { useEffect, useState, useRef } from "react";
import CreatableSelect from "react-select/creatable";
import AppConst from "../apis/appconst";
import axios from "axios";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";

const AddMedicine = ({ item, onAddDepart }) => {
  const [formData, setFormData] = useState({
    cats: [], units: [], purposes: [],
    med_id: "", med_name: "", med_sel_category: "", med_select_unit: "",
    med_select_purpose: "", med_pkg_number: "", med_hsn_code: "",
    med_central_tax: "", med_state_tax: "", med_comp_name: "",
    med_composition: "", med_in_strips: "1", med_user: "",
  });
  const [ldata, setLdata] = useState(null);

  // ── Refs for Enter navigation ──
  const medNameRef = useRef(null);
  const catRef = useRef(null);
  const purposeRef = useRef(null);
  const unitRef = useRef(null);
  const pkgRef = useRef(null);
  const hsnRef = useRef(null);
  const centralTaxRef = useRef(null);
  const stateTaxRef = useRef(null);
  const compositionRef = useRef(null);
  const stripsRef = useRef(null);
  const submitBtnRef = useRef(null);

  // Field order for Enter navigation
  const fieldRefs = [medNameRef, catRef, purposeRef, unitRef, pkgRef, hsnRef, centralTaxRef, stateTaxRef, compositionRef, stripsRef];

  // ── Helper: get portal target (works in both normal + fullscreen) ──
  const getPortalTarget = () => typeof document !== 'undefined' ? (document.fullscreenElement || document.body) : null;
  const getSwalTarget = () => document.fullscreenElement || document.body;

  // ── Enter key handler for TEXT fields ──
  // Select fields handle navigation via their onChange (auto-focus next on selection)
  const handleFieldEnter = (e, fieldIndex) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = fieldIndex + 1;
    if (next < fieldRefs.length) {
      const nextRef = fieldRefs[next];
      if (!nextRef.current) return;
      // Check if it's a react-select ref (has openMenu method)
      if (nextRef.current.openMenu) {
        nextRef.current.focus();
        setTimeout(() => nextRef.current.openMenu('first'), 50);
      } else if (nextRef.current.focus) {
        nextRef.current.focus();
        if (nextRef.current.select) nextRef.current.select();
      }
    } else {
      // Last field → Save
      submitBtnRef.current?.click();
    }
  };

  // ── Auto-focus medicine name when modal opens ──
  // ── Reset only the input fields (keep loaded options) ──
  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      med_id: "", med_name: "", med_sel_category: "", med_select_unit: "",
      med_select_purpose: "", med_pkg_number: "", med_hsn_code: "",
      med_central_tax: "", med_state_tax: "", med_comp_name: "",
      med_composition: "", med_in_strips: "1", med_user: "",
    }));
  };

  // ── Clear form + auto-focus medicine name whenever modal opens ──
  useEffect(() => {
    const modalEl = document.getElementById('addMedicineModal');
    if (!modalEl) return;
    const handler = () => {
      if (!item) resetForm();          // don't wipe when editing an existing item
      setTimeout(() => medNameRef.current?.focus(), 300);
    };
    modalEl.addEventListener('shown.bs.modal', handler);
    return () => modalEl.removeEventListener('shown.bs.modal', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // ── Option converters ──
  const toCatOptions = (records = []) => records.map(r => ({ value: String(r.cat_id), label: r.cat_name }));
  const toUnitOptions = (records = []) => records.map(r => ({ value: String(r.unit_id ?? r.cat_id ?? r.id ?? ""), label: r.unit_name ?? r.cat_name ?? r.name ?? "" }));
  const toPurposeOptions = (records = []) => records.map(r => ({ value: String(r.cat_id), label: r.cat_name }));

  // ── API calls ──
  const loadUnits = async (userId = 1) => {
    try {
      const fd = new FormData(); fd.append("page_no", "none");
      const resp = await axios.post(AppConst.BASE_URL + "/Webapis/get_unit_list", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (resp?.data?.status) { const opts = toUnitOptions(resp.data.records || []); setFormData(prev => ({ ...prev, units: opts })); return opts; }
      setFormData(prev => ({ ...prev, units: [] })); return [];
    } catch (err) { console.error("loadUnits:", err); setFormData(prev => ({ ...prev, units: [] })); return []; }
  };

  const loadCats = async (userId = 1) => {
    try {
      const fd = new FormData(); fd.append("cat_type", "medcats"); fd.append("page_no", "none"); fd.append("search_key", ""); fd.append("par_by", userId + "");
      const resp = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (resp.data?.status) { const opts = toCatOptions(resp.data.records || []); setFormData(prev => ({ ...prev, cats: opts })); return opts; }
      setFormData(prev => ({ ...prev, cats: [] })); return [];
    } catch (err) { console.error("loadCats:", err); setFormData(prev => ({ ...prev, cats: [] })); return []; }
  };

  const loadPurposes = async (userId = 1) => {
    try {
      const fd = new FormData(); fd.append("cat_type", "med_purpose"); fd.append("page_no", "none"); fd.append("search_key", ""); fd.append("par_by", userId + "");
      const resp = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (resp.data?.status) { const opts = toPurposeOptions(resp.data.records || []); setFormData(prev => ({ ...prev, purposes: opts })); return opts; }
      setFormData(prev => ({ ...prev, purposes: [] })); return [];
    } catch (err) { console.error("loadPurposes:", err); setFormData(prev => ({ ...prev, purposes: [] })); return []; }
  };

  const createCategoryApi = async (name) => { const fd = new FormData(); fd.append("cat_name", name); fd.append("cat_type", "medcats"); fd.append("par_by", ldata?.user_id || 1); return (await axios.post(AppConst.BASE_URL + AppConst.addCatUrl, fd, { headers: { "Content-Type": "multipart/form-data" } }))?.data; };
  const createPurposeApi = async (name) => { const fd = new FormData(); fd.append("cat_name", name); fd.append("cat_type", "med_purpose"); fd.append("par_by", ldata?.user_id || 1); return (await axios.post(AppConst.BASE_URL + AppConst.addCatUrl, fd, { headers: { "Content-Type": "multipart/form-data" } }))?.data; };
  const createUnitApi = async (name) => { const fd = new FormData(); fd.append("unit_name", name); fd.append("par_by", ldata?.user_id || 1); return (await axios.post(AppConst.BASE_URL + AppConst.addunitmed, fd, { headers: { "Content-Type": "multipart/form-data" } }))?.data; };

  // ── Init ──
  const initData = async () => {
    const stored = sessionStorage.getItem("ldata"); if (!stored) return;
    const parsed = JSON.parse(stored); setLdata(parsed);
    await Promise.all([loadCats(parsed?.user_id || 1), loadUnits(), loadPurposes(parsed?.user_id || 1)]);
  };

  useEffect(() => {
    const stored = sessionStorage.getItem("ldata"); if (stored) setLdata(JSON.parse(stored));
    initData();
    if (item) {
      setFormData(prev => ({ ...prev, med_id: item.med_id || "", med_name: item.med_name || "", med_sel_category: item.med_sel_category ? String(item.med_sel_category) : "", med_select_unit: item.med_select_unit ? String(item.med_select_unit) : "", med_select_purpose: item.med_select_purpose ? String(item.med_select_purpose) : "", med_pkg_number: item.med_pkg_number || "", med_hsn_code: item.med_hsn_code || "", med_central_tax: item.med_central_tax || "", med_state_tax: item.med_state_tax || "", med_comp_name: item.med_comp_name || "", med_composition: item.med_composition || "", med_in_strips: item.med_in_strips || "1", med_user: item.med_user || "" }));
    } else {
      setFormData(prev => ({ ...prev, med_id: "", med_name: "", med_sel_category: "", med_select_unit: "", med_select_purpose: "", med_pkg_number: "", med_hsn_code: "", med_central_tax: "", med_state_tax: "", med_comp_name: "", med_composition: "", med_in_strips: "1", med_user: "" }));
    }
  }, [item]);

  // ── Modal helpers ──
  const hideBootstrapModal = () => {
    const el = document.getElementById("addMedicineModal"); if (!el) return false;
    el.classList.add("bs-modal-temporary-hidden");
    document.body.classList.remove("modal-open");
    const bd = document.querySelector(".modal-backdrop"); if (bd) bd.style.display = "none";
    return true;
  };
  const restoreBootstrapModal = () => {
    const el = document.getElementById("addMedicineModal"); if (!el) return;
    el.classList.remove("bs-modal-temporary-hidden");
    const bd = document.querySelector(".modal-backdrop"); if (bd) bd.style.display = "";
  };

  // ── Change handlers ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    const central = parseFloat(name === "med_central_tax" ? value : formData.med_central_tax) || 0;
    const state = parseFloat(name === "med_state_tax" ? value : formData.med_state_tax) || 0;
    updated.med_comp_name = (central + state).toString();
    setFormData(updated);
  };
  // When select value chosen → auto-focus + open next field
  const focusNextSelect = (ref) => {
    setTimeout(() => {
      if (ref.current?.focus) ref.current.focus();
      if (ref.current?.openMenu) setTimeout(() => ref.current.openMenu('first'), 50);
    }, 100);
  };
  const focusNextInput = (ref) => {
    setTimeout(() => { if (ref.current) { ref.current.focus(); ref.current.select?.(); } }, 100);
  };

  const handleSelectCategory = (opt) => {
    setFormData(prev => ({ ...prev, med_sel_category: opt ? opt.value : "" }));
    if (opt) focusNextSelect(purposeRef);
  };
  const handleSelectPurpose = (opt) => {
    setFormData(prev => ({ ...prev, med_select_purpose: opt ? opt.value : "" }));
    if (opt) focusNextSelect(unitRef);
  };
  const handleSelectUnit = (opt) => {
    setFormData(prev => ({ ...prev, med_select_unit: opt ? opt.value : "" }));
    if (opt) focusNextInput(pkgRef);
  };


  // ── Robust close: use Bootstrap's Modal instance, then clean up ──
 // ── Robust close: use Bootstrap's Modal instance, then clean up ──
 const closeAddMedicineModal = () => {
  const el = document.getElementById("addMedicineModal");
  if (!el) return;

  try {
    const instance = Modal.getOrCreateInstance(el);
    instance.hide();
  } catch (err) {
    // fallback
    document.getElementById("closeBt45")?.click();
  }

  // Safety: remove leftover backdrop / body lock after the hide animation
  setTimeout(() => {
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach(b => b.remove());
  }, 300);
};
  // ── Creatable handlers ──
  const onCreateCategory = async (inputValue) => {
    if (!inputValue?.trim()) return;
    try {
      const res = await createCategoryApi(inputValue.trim());
      if (res?.status) {
        if (res.cat_dt) { const dt = Array.isArray(res.cat_dt) ? res.cat_dt[0] || res.cat_dt : res.cat_dt; const o = { value: String(dt.cat_id), label: dt.cat_name }; setFormData(prev => ({ ...prev, cats: [...prev.cats, o], med_sel_category: o.value })); }
        else { const opts = await loadCats(ldata?.user_id || 1); const f = opts.find(o => o.label.toLowerCase() === inputValue.toLowerCase()); if (f) setFormData(prev => ({ ...prev, med_sel_category: f.value })); }
        Swal.fire({ icon: "success", title: "Category added", timer: 900, showConfirmButton: false, target: getSwalTarget() });
      } else Swal.fire({ icon: "error", title: "Failed", text: res?.success || "Error", target: getSwalTarget() });
    } catch (err) { Swal.fire({ icon: "error", title: "Failed", text: String(err?.message || err), target: getSwalTarget() }); }
  };

  const onCreatePurpose = async (inputValue) => {
    if (!inputValue?.trim()) return;
    try {
      const res = await createPurposeApi(inputValue.trim());
      if (res?.status) {
        if (res.cat_dt) { const dt = Array.isArray(res.cat_dt) ? res.cat_dt[0] || res.cat_dt : res.cat_dt; const o = { value: String(dt.cat_id), label: dt.cat_name }; setFormData(prev => ({ ...prev, purposes: [...prev.purposes, o], med_select_purpose: o.value })); }
        else { const opts = await loadPurposes(ldata?.user_id || 1); const f = opts.find(o => o.label.toLowerCase() === inputValue.toLowerCase()); if (f) setFormData(prev => ({ ...prev, med_select_purpose: f.value })); }
        Swal.fire({ icon: "success", title: "Purpose added", timer: 900, showConfirmButton: false, target: getSwalTarget() });
      } else Swal.fire({ icon: "error", title: "Failed", text: res?.success || "Error", target: getSwalTarget() });
    } catch (err) { Swal.fire({ icon: "error", title: "Failed", text: String(err?.message || err), target: getSwalTarget() }); }
  };

  const onCreateUnit = async (inputValue) => {
    if (!inputValue?.trim()) return;
    try {
      const res = await createUnitApi(inputValue.trim());
      if (res?.status) {
        const u = res.unit;
        if (u && (u.unit_id || u.unitId || u.id)) { const o = { value: String(u.unit_id ?? u.unitId ?? u.id), label: u.unit_name ?? inputValue }; setFormData(prev => ({ ...prev, units: [...prev.units, o], med_select_unit: o.value })); }
        else { const opts = await loadUnits(); const f = opts.find(o => o.label.toLowerCase() === inputValue.toLowerCase()); if (f) setFormData(prev => ({ ...prev, med_select_unit: f.value })); }
        Swal.fire({ icon: "success", title: "Unit added", timer: 900, showConfirmButton: false, target: getSwalTarget() });
      } else Swal.fire({ icon: "error", title: "Failed", text: res?.success || "Error", target: getSwalTarget() });
    } catch (err) { Swal.fire({ icon: "error", title: "Failed", text: String(err?.message || err), target: getSwalTarget() }); }
  };

  // ── + button prompts (fixed target for fullscreen) ──
  const promptAddCategory = async () => {
    const hid = hideBootstrapModal();
    try {
      const { value: catName } = await Swal.fire({ title: "Add new category", input: "text", inputPlaceholder: "Category name", showCancelButton: true, confirmButtonText: "Add", target: getSwalTarget(), customClass: { container: 'swal-on-top' }, didOpen: () => Swal.getInput()?.focus(), preConfirm: v => { if (!v?.trim()) Swal.showValidationMessage("Required"); return v?.trim(); } });
      if (catName) await onCreateCategory(catName);
    } finally { if (hid) restoreBootstrapModal(); }
  };

  const promptAddPurpose = async () => {
    const hid = hideBootstrapModal();
    try {
      const { value: name } = await Swal.fire({ title: "Add new purpose", input: "text", inputPlaceholder: "Purpose name", showCancelButton: true, confirmButtonText: "Add", target: getSwalTarget(), customClass: { container: 'swal-on-top' }, didOpen: () => Swal.getInput()?.focus(), preConfirm: v => { if (!v?.trim()) Swal.showValidationMessage("Required"); return v?.trim(); } });
      if (name) await onCreatePurpose(name);
    } finally { if (hid) restoreBootstrapModal(); }
  };

  const promptAddUnit = async () => {
    const hid = hideBootstrapModal();
    try {
      const { value: name } = await Swal.fire({ title: "Add new unit", input: "text", inputPlaceholder: "Unit name", showCancelButton: true, confirmButtonText: "Add", target: getSwalTarget(), customClass: { container: 'swal-on-top' }, didOpen: () => Swal.getInput()?.focus(), preConfirm: v => { if (!v?.trim()) Swal.showValidationMessage("Required"); return v?.trim(); } });
      if (name) await onCreateUnit(name);
    } finally { if (hid) restoreBootstrapModal(); }
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    // ✅ Required-field validation
    if (!formData.med_name?.trim()) {
      Swal.fire({ icon: "warning", title: "Medicine name required", target: getSwalTarget() });
      medNameRef.current?.focus();
      return;
    }
    if (!formData.med_sel_category) {
      Swal.fire({ icon: "warning", title: "Please select a Category", target: getSwalTarget() });
      catRef.current?.focus();
      return;
    }
    if (!formData.med_select_unit) {
      Swal.fire({ icon: "warning", title: "Please select a Unit", target: getSwalTarget() });
      unitRef.current?.focus();
      return;
    }
    if (!formData.med_hsn_code?.trim()) {
      Swal.fire({ icon: "warning", title: "HSN Code required", target: getSwalTarget() });
      hsnRef.current?.focus();
      return;
    }
    if (formData.med_central_tax === "" || formData.med_central_tax === null) {
      Swal.fire({ icon: "warning", title: "CGST required", target: getSwalTarget() });
      centralTaxRef.current?.focus();
      return;
    }
    if (formData.med_state_tax === "" || formData.med_state_tax === null) {
      Swal.fire({ icon: "warning", title: "SGST required", target: getSwalTarget() });
      stateTaxRef.current?.focus();
      return;
    }
    const fd = new FormData();
    fd.append("med_id", formData.med_id); fd.append("med_name", formData.med_name);
    fd.append("med_sel_category", formData.med_sel_category); fd.append("med_select_unit", formData.med_select_unit);
    fd.append("med_select_purpose", formData.med_select_purpose); fd.append("med_pkg_number", formData.med_pkg_number);
    fd.append("med_hsn_code", formData.med_hsn_code); fd.append("med_central_tax", formData.med_central_tax);
    fd.append("med_state_tax", formData.med_state_tax); fd.append("med_comp_name", formData.med_comp_name);
    fd.append("med_composition", formData.med_composition); fd.append("med_in_strips", formData.med_in_strips);
    fd.append("med_user", ldata?.user_id || 1);
    try {
      const resp = await axios.post(AppConst.BASE_URL + AppConst.addMedUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (resp.data.status) {
        closeAddMedicineModal();   // ← was document.getElementById("closeBt45")?.click();
        resetForm();   
        onAddDepart && onAddDepart();
        Swal.fire({ icon: "success", title: "Saved", timer: 1200, showConfirmButton: false, target: getSwalTarget() });
      } else Swal.fire({ icon: "error", title: "Failed", text: resp.data.success || "Error", target: getSwalTarget() });
    } catch (err) { Swal.fire({ icon: "error", title: "Failed", text: String(err?.message || err), target: getSwalTarget() }); }
  };

  const rsStyles = {
    control: base => ({ ...base, minHeight: "38px", borderRadius: "4px" }),
    menu: base => ({ ...base, zIndex: 99999 }),
    menuPortal: base => ({ ...base, zIndex: 99999 }),
  };
  const findOption = (options, val) => options.find(o => String(o.value) === String(val)) || null;

  return (
    <div className="modal fade" id="addMedicineModal" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-light p-3">
            <h5 className="modal-title">Add Medicine</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" id="close-modal" />
          </div>
          <form className="tablelist-form" onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row gy-4 mb-3">

                {/* Medicine Name — auto-focused, Enter → Category */}
                <div className="col-md-4">
                  <label className="form-label">Medicine Name</label>
                  <input ref={medNameRef} name="med_name" value={formData.med_name}
                    onChange={e => setFormData(prev => ({ ...prev, med_name: e.target.value }))}
                    onKeyDown={e => handleFieldEnter(e, 0)}
                    className="form-control" placeholder="Enter Medicine name" type="text" autoFocus />
                </div>

                {/* Category — Enter → Purpose */}
                <div className="col-md-4">
                  <label className="form-label">Select Category</label>
                  <div className="d-flex gap-2 align-items-center">
                    <div style={{ flex: 1 }}>
                      <CreatableSelect ref={catRef} isClearable styles={rsStyles}
                        placeholder="Select or type..." options={formData.cats}
                        value={findOption(formData.cats, formData.med_sel_category)}
                        onChange={handleSelectCategory} onCreateOption={onCreateCategory}
                        menuPortalTarget={getPortalTarget()} menuPosition="fixed" />
                    </div>
                    <button type="button" className="btn btn-outline-secondary" title="Add category" onClick={promptAddCategory}>+</button>
                  </div>
                </div>

                {/* Purpose — Enter → Unit */}
                <div className="col-md-4">
                  <label className="form-label">Medicine Purpose</label>
                  <div className="d-flex gap-2 align-items-center">
                    <div style={{ flex: 1 }}>
                      <CreatableSelect ref={purposeRef} isClearable styles={rsStyles}
                        placeholder="Select purpose..." options={formData.purposes}
                        value={findOption(formData.purposes, formData.med_select_purpose)}
                        onChange={handleSelectPurpose} onCreateOption={onCreatePurpose}
                        menuPortalTarget={getPortalTarget()} menuPosition="fixed" />
                    </div>
                    <button type="button" className="btn btn-outline-secondary" title="Add purpose" onClick={promptAddPurpose}>+</button>
                  </div>
                </div>

                {/* Unit — Enter → Package */}
                <div className="col-md-4">
                  <label className="form-label">Select Unit</label>
                  <div className="d-flex gap-2 align-items-center">
                    <div style={{ flex: 1 }}>
                      <CreatableSelect ref={unitRef} isClearable styles={rsStyles}
                        placeholder="Select unit..." options={formData.units}
                        value={findOption(formData.units, formData.med_select_unit)}
                        onChange={handleSelectUnit} onCreateOption={onCreateUnit}
                        menuPortalTarget={getPortalTarget()} menuPosition="fixed" />
                    </div>
                    <button type="button" className="btn btn-outline-secondary" title="Add unit" onClick={promptAddUnit}>+</button>
                  </div>
                </div>

                {/* Package Number */}
                <div className="col-md-4">
                  <label className="form-label">Package Number</label>
                  <input ref={pkgRef} name="med_pkg_number" value={formData.med_pkg_number}
                    onChange={e => setFormData(prev => ({ ...prev, med_pkg_number: e.target.value }))}
                    onKeyDown={e => handleFieldEnter(e, 4)}
                    className="form-control" placeholder="Enter Package Number" type="text" />
                </div>

                {/* HSN Code */}
                <div className="col-md-4">
                  <label className="form-label">Hsn Code</label>
                  <input ref={hsnRef} name="med_hsn_code" value={formData.med_hsn_code}
                    onChange={e => setFormData(prev => ({ ...prev, med_hsn_code: e.target.value }))}
                    onKeyDown={e => handleFieldEnter(e, 5)}
                    className="form-control" placeholder="Enter Hsn Code" type="text" />
                </div>

                {/* Central Tax */}
                <div className="col-md-4">
                  <label className="form-label">CGST</label>
                  <input ref={centralTaxRef} name="med_central_tax" value={formData.med_central_tax}
                    onChange={handleChange}
                    onKeyDown={e => handleFieldEnter(e, 6)}
                    className="form-control" placeholder="Central Tax" type="number" step="0.01" />
                </div>

                {/* State Tax */}
                <div className="col-md-4">
                  <label className="form-label">SGST</label>
                  <input ref={stateTaxRef} name="med_state_tax" value={formData.med_state_tax}
                    onChange={handleChange}
                    onKeyDown={e => handleFieldEnter(e, 7)}
                    className="form-control" placeholder="State Tax" type="number" step="0.01" />
                </div>

                {/* Integrated Tax (readonly) */}
                <div className="col-md-4">
                  <label className="form-label">IGST</label>
                  <input name="med_comp_name" readOnly value={formData.med_comp_name}
                    className="form-control" placeholder="Integrated Tax" type="text"
                    tabIndex={-1} />
                </div>

                {/* Composition */}
                <div className="col-md-4">
                  <label className="form-label">Medicine Composition</label>
                  <input ref={compositionRef} name="med_composition" value={formData.med_composition}
                    onChange={e => setFormData(prev => ({ ...prev, med_composition: e.target.value }))}
                    onKeyDown={e => handleFieldEnter(e, 8)}
                    className="form-control" placeholder="Enter Composition" type="text" />
                </div>

                {/* In Strips — last field, Enter = Save */}
                <div className="col-md-4">
                  <label className="form-label">Medicine In Strips</label>
                  <input ref={stripsRef} name="med_in_strips" value={formData.med_in_strips}
                    onChange={e => setFormData(prev => ({ ...prev, med_in_strips: e.target.value }))}
                    onKeyDown={e => handleFieldEnter(e, 9)}
                    className="form-control" placeholder="In Strips" type="text" />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="hstack gap-2 justify-content-end">
                <button type="button" id="closeBt45" className="btn btn-light" data-bs-dismiss="modal">Close</button>
                <button ref={submitBtnRef} type="submit" className="btn btn-success" id="edit-btn">
                  {formData.med_id ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMedicine;